import React, { useRef, useState } from 'react';
import axios from 'axios';
import 'iconify-icon';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * EneaflixBulkUpload
 * ------------------
 * Drag-and-drop / picker panel that accepts many poster images at once
 * and POSTs them to `/api/admin/eneaflix/bulk-poster-upload`. The server
 * normalizes each filename, fuzzy-matches it against existing Eneaflix
 * titles and updates the `image` field automatically. The response is
 * surfaced as a clear matched / unmatched / errors summary so the admin
 * knows exactly which files were associated.
 */
const EneaflixBulkUpload = ({ onComplete }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    setReport(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await axios.post(
        `${API_URL}/admin/eneaflix/bulk-poster-upload`,
        fd,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setReport(res.data);
      const s = res.data.summary || {};
      toast.success(
        `Upload concluído: ${s.matched || 0}/${s.received || 0} associados` +
          (s.unmatched ? ` · ${s.unmatched} sem match` : '') +
          (s.errors ? ` · ${s.errors} erros` : '')
      );
      if (onComplete) onComplete(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no upload em massa');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="mb-8" data-testid="eneaflix-bulk-upload">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`border-2 border-dashed rounded-2xl p-8 transition-colors text-center ${
          dragOver
            ? 'border-accent-orange bg-orange-50'
            : 'border-neutral-300 bg-neutral-50'
        }`}
      >
        <iconify-icon
          icon="solar:upload-square-linear"
          className="text-5xl text-accent-orange mb-3"
        ></iconify-icon>
        <h3 className="font-semibold text-lg mb-1">Upload em massa de pôsteres</h3>
        <p className="text-sm text-neutral-600 mb-4 max-w-xl mx-auto">
          Arraste vários arquivos aqui ou clique para selecionar. O sistema
          associa cada arquivo ao filme/série pelo nome (ignora acentos,
          maiúsculas, hífens e underscores). Ex.: <code>a-teoria-de-tudo.jpg</code>{' '}
          → <strong>A Teoria de Tudo</strong>.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="eneaflix-bulk-file-input"
        />
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="eneaflix-bulk-pick-btn"
        >
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <iconify-icon icon="solar:refresh-linear" className="animate-spin"></iconify-icon>
              Enviando…
            </span>
          ) : (
            'Selecionar imagens'
          )}
        </Button>
      </div>

      {report && (
        <div
          className="mt-4 bg-white rounded-xl border border-neutral-200 divide-y"
          data-testid="eneaflix-bulk-report"
        >
          <div className="p-4 flex flex-wrap items-center gap-4 text-sm">
            <span className="font-semibold">Resultado:</span>
            <span className="text-emerald-600">
              ✓ {report.summary.matched} associados
            </span>
            {report.summary.unmatched > 0 && (
              <span className="text-amber-600">
                ⚠ {report.summary.unmatched} sem match
              </span>
            )}
            {report.summary.errors > 0 && (
              <span className="text-red-600">
                ✗ {report.summary.errors} erros
              </span>
            )}
          </div>

          {report.matched && report.matched.length > 0 && (
            <div className="p-4">
              <h4 className="text-xs font-mono uppercase tracking-widest text-emerald-700 mb-2">
                Associados
              </h4>
              <ul className="space-y-1 text-sm">
                {report.matched.map((m) => (
                  <li key={m.eneaflix_id} className="flex items-center gap-3">
                    <img
                      src={m.url}
                      alt=""
                      className="w-10 h-14 object-cover rounded border border-neutral-200"
                    />
                    <div>
                      <div className="font-medium">{m.title}</div>
                      <div className="text-xs text-neutral-500">{m.file}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.unmatched && report.unmatched.length > 0 && (
            <div className="p-4">
              <h4 className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-2">
                Sem match — renomeie e tente novamente
              </h4>
              <ul className="space-y-1 text-sm text-neutral-700">
                {report.unmatched.map((u) => (
                  <li key={u.file} className="flex justify-between items-center">
                    <span>{u.file}</span>
                    <code className="text-xs text-neutral-500">
                      normalizado: &quot;{u.normalized}&quot;
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.errors && report.errors.length > 0 && (
            <div className="p-4">
              <h4 className="text-xs font-mono uppercase tracking-widest text-red-700 mb-2">
                Erros
              </h4>
              <ul className="space-y-1 text-sm text-red-700">
                {report.errors.map((er) => (
                  <li key={er.file}>
                    <span className="font-medium">{er.file}</span> — {er.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EneaflixBulkUpload;
